import { ReactElement, useEffect, useState } from "react";
import {
  BlueLink,
  NeverUnderlineSpace,
  Redirect,
  TextLink,
  useLocation,
} from "_/components/router";
import { t } from "@lingui/core/macro";
import { atom, useAtom, PrimitiveAtom } from "jotai";
import { assocPath, pipe, any, equals } from "ramda";
import { Temporal } from "temporal-polyfill";

import {
  Project,
  Revision,
  Optimization,
  useCreateOptimization,
  useProjectOptimizations,
  useReviseProject,
  useOptimizationCreatedRevision,
  StepStatus,
  useRevisionMaterials,
} from "_/data/projects";
import { blobDownloadURL, useBlobContents } from "_/data/blob";
import { useAddPrintQueueEntry } from "_/data/print-queue";
import { PermissionTest } from "_/components/permission-test";
import { permissions } from "_/data/rbac/definitions";

import {
  viewAtom,
  type SimViewControls,
  type SimInfo,
  type SimViewData,
  type SimResultSource,
} from "_/state/viewer";

import { Slider } from "_/components/slider";
import { RadioButton, RadioGroup } from "_/components/radio";
import { LabeledSlider } from "_/components/viewer-controls/sliders";
import { ProgressBar } from "_/components/progress-bar";
import {
  ButtonContainer,
  Modal,
  SubmitCancelButtons,
  ViewerOverlayNotice,
} from "_/components/modal";
import { docsUrls, projectUrls, useOptimizeRoute } from "_/routes";
import { useAddToast } from "_/components/toasts";
import { FileDownloadLink } from "_/components/file-download-link";
import { NumberBadge } from "_/components/number-badge";

import { Uuid, Tuple } from "_/types";

import { Button } from "_/components/button";
import { Alert } from "_/components/alert";
import { MaterialName, OptimizationIcon } from "_/components/materials";
import { palette } from "_/style";

import { SimOutput, parseSimResultBinary } from "./parseBin";
import { OptimizationStepSection } from "./optimization-step-section";
import { StepSymbol } from "./utils";
import { noop } from "_/utils";
import { dateTimeString, formatHourMinuteDuration } from "_/utils/datetime";

import * as PrepareS from "../prepare/styled";
import * as S from "./styled";

function getSimResultInfo(sim: SimOutput): SimInfo {
  const { xyzPositions, layerWelding, previousLayerTemperature } = sim;
  let minZ = Infinity;
  let maxZ = -Infinity;
  let minLayerWelding = Infinity;
  let maxLayerWelding = -Infinity;
  let minPreviousLayerTemperature = Infinity;
  let maxPreviousLayerTemperature = -Infinity;

  for (let i = 0; i < xyzPositions.length; i += 3) {
    const z = xyzPositions[i + 2];
    minZ = Math.min(minZ, z);
    maxZ = Math.max(maxZ, z);
  }

  for (let i = 0; i < layerWelding.length; i++) {
    const welding = layerWelding[i];

    // 0 denotes invalid data, so it is ignored when computing the range
    const nonZeroWelding = welding > 0;
    minLayerWelding = Math.min(
      minLayerWelding,
      nonZeroWelding ? welding : Infinity
    );
    maxLayerWelding = Math.max(
      maxLayerWelding,
      nonZeroWelding ? welding : -Infinity
    );
  }

  for (let i = 0; i < previousLayerTemperature.length; i++) {
    const temp = previousLayerTemperature[i];

    // 0 denotes invalid data, so it is ignored when computing the range
    const nonZeroTemp = temp > 0;
    minPreviousLayerTemperature = Math.min(
      minPreviousLayerTemperature,
      nonZeroTemp ? temp : Infinity
    );
    maxPreviousLayerTemperature = Math.max(
      maxPreviousLayerTemperature,
      nonZeroTemp ? temp : -Infinity
    );
  }

  return {
    zRange: [minZ, maxZ],
    layerWelding: [minLayerWelding, maxLayerWelding],
    previousLayerTemperature: [
      minPreviousLayerTemperature,
      maxPreviousLayerTemperature,
    ],
  };
}

// Parse the sim result blob and return the data + metadata in the forms needed for the viewer and controls
function unpackSimResultBuffer(simBinary: ArrayBuffer): {
  simResultInfo: SimInfo;
  simResultSource: SimResultSource;
} | null {
  const parsedSimData = parseSimResultBinary(simBinary);

  // Return null if parsing fails
  if (!parsedSimData) {
    return null;
  }

  const simResultInfo = getSimResultInfo(parsedSimData);
  const simResultSource: SimResultSource = {
    positions: parsedSimData.xyzPositions,
    layerWelding: parsedSimData.layerWelding,
    previousLayerTemperature: parsedSimData.previousLayerTemperature,
  };

  return { simResultInfo, simResultSource };
}

type SimControlsProps = {
  project: Project;

  optimization: Optimization;

  /** Jotai atom to access the control state for the thermal visualization. */
  controlsAtom: PrimitiveAtom<SimViewControls>;
};

/**
 * Renders controls for the thermal point cloud.
 */
const SimControls = ({
  project,
  optimization,
  controlsAtom,
}: SimControlsProps): ReactElement => {
  const [controls, setControls] = useAtom(controlsAtom);
  const addToast = useAddToast();
  const reviseProject = useReviseProject();
  const [_location, setLocation] = useLocation();

  const revision = project.revisions.find(
    (r) => r.id === optimization?.revisionId
  );
  const { data: optimizationCreatedRevision } = useOptimizationCreatedRevision(
    project,
    optimization
  );

  const originalGcodeRecord = revision?.gcodeFile?.file;
  const optimizedGcodeRecord = optimization?.optimizedGcode ?? undefined;

  if (!controls) {
    return <></>;
  }
  const { source, snapshot } = controls;

  const zRangeRange = controls.ranges.zRange;

  const zRangeValues = controls.values.zRange;

  const valueRangeRange = controls.ranges[snapshot];

  // Use this as both the valueRange value and the colorRange range
  const valueRangeValues = controls.values[snapshot].valueRange;

  const colorRangeValues = controls.values[snapshot].colorRange;

  const showOptimizedOption = controls.includesOptimized;

  function setSource(value: string) {
    setControls(assocPath(["source"], value));
  }

  function setSnapshot(value: string) {
    setControls(assocPath(["snapshot"], value));
  }

  async function createRevisionFromOptimized() {
    if (!optimizedGcodeRecord) {
      console.error("Optimized G-code result is unexpectedly missing");
      return; // Async function, so exceptions can't be caught by the caller.
    }
    await reviseProject.mutateAsync({
      projectId: project.id,
      revisionChange: { gcode: optimizedGcodeRecord },
      onSuccess: (newRevision: Revision) => {
        addToast({
          title: t`components.viewer-controls-optimize.revision-number-created ${newRevision.number}`,
          kind: "success",
          timeout: 5_000,
        });

        setLocation(projectUrls.prepare(project.id, newRevision.id));
      },
    });
  }

  const optimizationString = t`common.optimization`;
  const originalGcodeString = t`components.viewer-controls.original-gcode`;
  const optimizationOptimizedString = t`components.viewer-controls.optimization-comparison`;
  const simulatedMeasuresString = t`components.viewer-controls.simulated-measures`;
  const layerTemperatureString = t`components.viewer-controls.layer-temperature`;
  const layerWeldingString = t`components.viewer-controls.layer-welding`;

  return (
    <>
      <S.LayerControlContainer>
        <S.ControlLabel>{t`common.layer`}</S.ControlLabel>
        <S.LayerSliderContainer>
          <Slider
            min={zRangeRange[0]}
            max={zRangeRange[1]}
            step={0.5}
            value={zRangeValues}
            size="large"
            vertical
            tooltipPlacement="before"
            tooltipText={(values) => values.map((v) => `Z: ${v.toFixed(2)}mm`)}
            onChange={(values) => {
              setControls(assocPath(["values", "zRange"], values));
            }}
          />
        </S.LayerSliderContainer>
      </S.LayerControlContainer>
      <S.RightSideContainer>
        <S.PointCloudControlContainer>
          <S.ControlsBox>
            {originalGcodeRecord?.id && revision && (
              <S.ControlsBoxSection>
                <S.ControlsBoxTitle>
                  {t`components.viewer-controls.original-gcode`}{" "}
                  <FileDownloadLink
                    href={blobDownloadURL(originalGcodeRecord.id)}
                    title={originalGcodeRecord.name}
                  />
                </S.ControlsBoxTitle>
                <TextLink
                  variant="default"
                  to={projectUrls.prepare(project.id, revision.id)}
                >
                  <span>{t`common.revision`}</span>
                  <NeverUnderlineSpace />
                  <NumberBadge number={revision.number} size="md" />
                </TextLink>
              </S.ControlsBoxSection>
            )}
            {optimizedGcodeRecord?.id && (
              <S.ControlsBoxSection>
                <S.ControlsBoxTitle>
                  {t`common.optimized-gcode`}
                  <FileDownloadLink
                    href={blobDownloadURL(optimizedGcodeRecord.id)}
                    title={optimizedGcodeRecord.name}
                  />
                </S.ControlsBoxTitle>
                {optimizationCreatedRevision ? (
                  <TextLink
                    variant="default"
                    to={projectUrls.prepare(
                      project.id,
                      optimizationCreatedRevision.id
                    )}
                  >
                    <span>{t`common.revision`}</span>
                    <NeverUnderlineSpace />
                    <NumberBadge
                      number={optimizationCreatedRevision.number}
                      size="md"
                    />
                  </TextLink>
                ) : (
                  <PermissionTest
                    requiredPermissions={permissions.projects.write}
                  >
                    <S.CreateRevisionButton
                      kind="primary"
                      onClick={() => void createRevisionFromOptimized()}
                    >
                      {t`components.controls.optimize.create-revision`}
                    </S.CreateRevisionButton>
                  </PermissionTest>
                )}
              </S.ControlsBoxSection>
            )}
          </S.ControlsBox>
        </S.PointCloudControlContainer>
        <S.PointCloudControlContainer>
          <S.ControlsBox>
            {optimizationString}
            <S.RadioOptions>
              <RadioGroup name="source" onChange={setSource} value={source}>
                <RadioButton label={originalGcodeString} value={"original"} />
                <RadioButton
                  label={optimizationOptimizedString}
                  value={"optimized"}
                  disabled={!showOptimizedOption}
                />
              </RadioGroup>
            </S.RadioOptions>
          </S.ControlsBox>
        </S.PointCloudControlContainer>
        <S.PointCloudControlContainer>
          <S.ControlsBox>
            {simulatedMeasuresString}
            <S.RadioOptions>
              <RadioGroup
                name="snapshot"
                onChange={setSnapshot}
                value={snapshot}
              >
                <RadioButton
                  label={layerTemperatureString}
                  value={"previousLayerTemperature"}
                />
                <RadioButton
                  label={layerWeldingString}
                  value={"layerWelding"}
                />
              </RadioGroup>
            </S.RadioOptions>
          </S.ControlsBox>
        </S.PointCloudControlContainer>
        <S.PointCloudControlContainer>
          <LabeledSlider
            key={`${snapshot}-colorRange`}
            label={t`common.color`}
            min={valueRangeValues[0]}
            max={valueRangeValues[1]}
            step={0.1}
            value={colorRangeValues}
            onChange={(values) => {
              setControls(
                assocPath(["values", snapshot, "colorRange"], values)
              );
            }}
          />
          <LabeledSlider
            key={`${snapshot}-valueRange`}
            label={t`common.value`}
            min={valueRangeRange[0]}
            max={valueRangeRange[1]}
            step={0.1}
            value={valueRangeValues}
            onChange={(values) => {
              setControls(
                pipe(
                  assocPath(["values", snapshot, "valueRange"], values),
                  assocPath(["values", snapshot, "colorRange"], values)
                )
              );
            }}
          />
        </S.PointCloudControlContainer>
      </S.RightSideContainer>
    </>
  );
};

type OptimizationResultLoaderProps = {
  project: Project;
  optimization: Optimization;
};

/**
 * Loads the optimization result data.
 */
const OptimizationResultLoader = ({
  project,
  optimization,
}: OptimizationResultLoaderProps): ReactElement => {
  const { data: comparisons = [] } = useProjectOptimizations(project.id, {
    parentId: optimization.job.id,
  });
  // Loading progress for the result data
  const [analysisProgress, setAnalysisProgress] = useState(0);
  const [optimizedProgress, setOptimizedProgress] = useState(0);

  const [_view, setView] = useAtom(viewAtom);

  const [controlsAtom, setControlsAtom] =
    useState<PrimitiveAtom<SimViewControls>>();

  const [parseFailed, setParseFailed] = useState(false);

  const original = comparisons.find(
    (o) => o.job.input.simulationRole === "original"
  );
  const optimized = comparisons.find(
    (o) => o.job.input.simulationRole === "optimized"
  );

  // New flow: analysis output lives on the child comparison jobs.
  // Legacy: output was stored directly on the parent optimization.
  const analysisBlobRecord =
    original?.analysisOutput ?? optimization.analysisOutput;
  const optimizedBlobRecord =
    optimized?.analysisOutput ?? optimization.optimizedOutput;

  const {
    data: analysisBuffer,
    error: analysisLoadingError,
    isFetched: analysisFetched,
  } = useBlobContents(
    analysisBlobRecord ?? undefined,
    setAnalysisProgress,
    true
  );

  const {
    data: optimizedBuffer,
    error: optimizedLoadingError,
    isFetched: optimizedFetched,
  } = useBlobContents(
    optimizedBlobRecord ?? undefined,
    setOptimizedProgress,
    true
  );

  useEffect(() => {
    if (analysisFetched) {
      setAnalysisProgress(1);
    }
  }, [analysisFetched]);

  useEffect(() => {
    if (optimizedFetched) {
      setOptimizedProgress(1);
    }
  }, [optimizedFetched]);

  const loadingError = !!analysisLoadingError || !!optimizedLoadingError;

  useEffect(() => {
    // Ensure the expected blobs are loaded before proceeding
    if (
      !analysisBuffer ||
      !analysisBlobRecord ||
      (optimizedBlobRecord && !optimizedBuffer)
    ) {
      return;
    }

    const loadSimView = () => {
      const analysisUnpacked = unpackSimResultBuffer(analysisBuffer);

      // Return early if parsing fails
      if (!analysisUnpacked) {
        setParseFailed(true);
        return;
      }

      const {
        simResultInfo: analysisInfo,
        simResultSource: analysisResultSource,
      } = analysisUnpacked;

      let optimizedResultSource: SimResultSource | undefined;

      const aggregateSimInfo = analysisInfo;

      if (optimizedBuffer) {
        const optimizedUnpacked = unpackSimResultBuffer(optimizedBuffer);

        if (optimizedUnpacked) {
          const optimizedInfo = optimizedUnpacked.simResultInfo;
          optimizedResultSource = optimizedUnpacked.simResultSource;

          const aggregateMinMax = (
            a: Tuple<number>,
            b: Tuple<number>
          ): Tuple<number> => {
            return [Math.min(a[0], b[0]), Math.max(a[1], b[1])];
          };

          // Aggregate the ranges for the analysis and optimized data
          const aggregateLayerWelding = aggregateMinMax(
            analysisInfo.layerWelding,
            optimizedInfo.layerWelding
          );

          const aggregatePreviousLayerTemperature = aggregateMinMax(
            analysisInfo.previousLayerTemperature,
            optimizedInfo.previousLayerTemperature
          );

          aggregateSimInfo.layerWelding = aggregateLayerWelding;
          aggregateSimInfo.previousLayerTemperature =
            aggregatePreviousLayerTemperature;
        }
      }

      const simViewData: SimViewData = {
        original: analysisResultSource,
        optimized: optimizedResultSource,
      };

      const cAtom = atom<SimViewControls>({
        source: "original",
        snapshot: "previousLayerTemperature",
        includesOptimized: !!optimizedBuffer,
        ranges: aggregateSimInfo,
        values: {
          zRange: aggregateSimInfo.zRange,
          layerWelding: {
            valueRange: aggregateSimInfo.layerWelding,
            colorRange: aggregateSimInfo.layerWelding,
          },
          previousLayerTemperature: {
            valueRange: aggregateSimInfo.previousLayerTemperature,
            colorRange: aggregateSimInfo.previousLayerTemperature,
          },
        },
      });

      setView({
        mode: "optimize",
        id: analysisBlobRecord.id,
        data: simViewData,
        controlsAtom: cAtom,
      });

      setControlsAtom(cAtom);
    };

    loadSimView();
  }, [
    analysisBuffer,
    optimizedBlobRecord,
    optimizedBuffer,
    setView,
    setControlsAtom,
    analysisBlobRecord,
  ]);

  if (parseFailed) {
    setView(null);
    return (
      <ViewerOverlayNotice
        title={t`components.controls.optimize.parse-failed-title`}
      >{t`components.controls.optimize.parse-failed-message`}</ViewerOverlayNotice>
    );
  }

  if (!controlsAtom) {
    return (
      <ViewerOverlayNotice
        title={t`components.controls.optimize.loading-optimization-data`}
      >
        <S.ProgressWrapper>
          <ProgressBar
            title={t`components.viewer-controls.original-gcode`}
            value={analysisProgress}
          />
          {optimizedBlobRecord && (
            <ProgressBar
              title={t`components.viewer-controls.optimization-comparison`}
              value={optimizedProgress}
            />
          )}
          {loadingError && t`components.controls.loading-error`}
        </S.ProgressWrapper>
      </ViewerOverlayNotice>
    );
  }

  return (
    <SimControls
      project={project}
      optimization={optimization}
      controlsAtom={controlsAtom}
    />
  );
};

// Returns the latest optimization for a specific job ID, or falls back to the
// latest optimization on the latest revision that has one.
function findLatestOptimization(
  revisions: Revision[],
  optimizations: Optimization[] | undefined,
  optimizationId?: Uuid
): Optimization | undefined {
  if (optimizationId) {
    return optimizations?.find((s) => s.job.id === optimizationId);
  }
  return revisions
    .map((r) => optimizations?.findLast((s) => s.revisionId === r.id))
    .at(-1);
}

function getStepErrorMessages(): Record<string, string> {
  return {
    dual_tool_pair_not_supported: t`components.controls.optimize.step.error.dual-tool-pair-not-supported`,
    speed_optimization_not_supported: t`components.controls.optimize.step.error.speed-optimization-not-supported`,
    material_not_supported: t`components.controls.optimize.step.error.material-not-supported`,
  };
}

function getStepWarningMessages(): Record<string, string> {
  return {
    pause_in_gcode: t`components.controls.optimize.step.warning.pause-in-gcode`,
  };
}

function getOptimizationSteps(): {
  step: string;
  kind: "check" | "optimization";
  text?: string;
  subtitles?: Partial<Record<StepStatus, string>>;
}[] {
  return [
    {
      step: "CHECK_GCODE_INTEGRITY",
      kind: "check",
      text: t`components.controls.optimize.step.check-gcode-integrity`,
      subtitles: {
        running: t`components.controls.optimize.step.check-gcode-integrity-subtitle-running`,
        completed: t`components.controls.optimize.step.check-gcode-integrity-subtitle-completed`,
        failed: t`components.controls.optimize.step.check-gcode-integrity-subtitle-failed`,
      },
    },
    {
      step: "CHECK_MOVE_BOUNDS",
      kind: "check",
      text: t`components.controls.optimize.step.check-move-bounds`,
      subtitles: {
        running: t`components.controls.optimize.step.check-move-bounds-subtitle-running`,
        completed: t`components.controls.optimize.step.check-move-bounds-subtitle-completed`,
        failed: t`components.controls.optimize.step.check-move-bounds-subtitle-failed`,
      },
    },
    {
      step: "CHECK_PART_THERMALS",
      kind: "check",
      text: t`components.controls.optimize.step.check-part-thermals`,
      subtitles: {
        running: t`components.controls.optimize.step.check-part-thermals-subtitle-running`,
        completed: t`components.controls.optimize.step.check-part-thermals-subtitle-completed`,
        failed: t`components.controls.optimize.step.check-part-thermals-subtitle-failed`,
      },
    },
    {
      step: "OPTIMIZE_THERMALS",
      kind: "optimization",
      text: t`components.controls.optimize.step.optimize-thermals`,
      subtitles: {
        running: t`components.controls.optimize.step.optimize-thermals-subtitle-running`,
        completed: t`components.controls.optimize.step.optimize-thermals-subtitle-completed`,
      },
    },
    {
      step: "OPTIMIZE_SPEED",
      kind: "optimization",
      text: t`components.controls.optimize.step.optimize-speed`,
      subtitles: {
        running: t`components.controls.optimize.step.optimize-speed-subtitle-running`,
        completed: t`components.controls.optimize.step.optimize-speed-subtitle-completed`,
      },
    },
    {
      step: "CHECK_MATERIALS",
      kind: "check",
      text: t`components.controls.optimize.step.check-materials`,
      subtitles: {
        running: t`components.controls.optimize.step.check-materials-subtitle-running`,
        completed: t`components.controls.optimize.step.check-materials-subtitle-completed`,
        failed: t`components.controls.optimize.step.check-materials-subtitle-failed`,
      },
    },
  ];
}

export const OptimizeQueueModal = ({
  project,
  optimization,
  revision,
  optimizedGcodeRecord,
  isOpen,
  onClose,
}: {
  project: Project;
  optimization: Optimization;
  revision: Revision;
  optimizedGcodeRecord: NonNullable<Optimization["optimizedGcode"]>;
  isOpen: boolean;
  onClose: () => void;
}): ReactElement => {
  const [selected, setSelected] = useState<"optimized" | "original">(
    "optimized"
  );
  const reviseProject = useReviseProject();
  const addQueueEntry = useAddPrintQueueEntry();
  const addToast = useAddToast();

  const isSubmitting = reviseProject.isPending || addQueueEntry.isPending;

  async function handleSubmit() {
    if (selected === "optimized") {
      const result = await reviseProject.mutateAsync({
        projectId: project.id,
        revisionChange: { gcode: optimizedGcodeRecord },
      });
      const newRevision = result.data.data;
      await addQueueEntry.mutateAsync({ revisionId: newRevision.id });
    } else {
      await addQueueEntry.mutateAsync({ revisionId: revision.id });
    }
    addToast({
      kind: "success",
      title: t`components.queue.add-success`,
      timeout: 5_000,
    });
    onClose();
  }

  return (
    <Modal
      open={isOpen}
      onClose={isSubmitting ? undefined : onClose}
      title={t`components.controls.optimize.queue-modal-title`}
    >
      <S.RadioOptions>
        <RadioGroup
          name="queue-choice"
          onChange={(v: "optimized" | "original") => setSelected(v)}
          value={selected}
        >
          <RadioButton
            value="optimized"
            label={
              <>
                {t`components.controls.optimize.queue-modal-option-optimized`}{" "}
                <S.RevisionOrJobNumber
                  title={t`common.job-description ${revision.number} ${optimization.number}`}
                >
                  #{revision.number}-OP{optimization.number}
                </S.RevisionOrJobNumber>
              </>
            }
          />
          <RadioButton
            value="original"
            label={
              <>
                {t`components.controls.optimize.queue-modal-option-original`}{" "}
                <S.RevisionOrJobNumber>
                  #{revision.number}
                </S.RevisionOrJobNumber>
              </>
            }
          />
        </RadioGroup>
      </S.RadioOptions>
      <SubmitCancelButtons
        submitButtonLabel={t`components.queue.add-button`}
        cancelButtonLabel={t`common.cancel`}
        onSubmit={handleSubmit}
        onCancel={isSubmitting ? undefined : onClose}
        disableSubmit={isSubmitting}
        disableCancel={isSubmitting}
      />
    </Modal>
  );
};

type OptimizationStepWithStatus = ReturnType<
  typeof getOptimizationSteps
>[number] & {
  status: StepStatus;
  progress: number;
  subtitle: string | undefined;
  timeSavedSec: number | null;
};

const OptimizationStepRow = ({
  step,
  isLegacy,
  project,
  optimization,
}: {
  step: OptimizationStepWithStatus;
  isLegacy: boolean;
  project: Project;
  optimization: Optimization;
}): ReactElement => {
  const hasSubstep =
    !isLegacy &&
    step.step === "OPTIMIZE_THERMALS" &&
    (step.status === "running" || step.status === "completed");

  const stepSubtitle =
    step.timeSavedSec !== null ? (
      <S.TimeSavedSubtitle>
        <S.TimeSavedValue>
          {formatHourMinuteDuration(
            Temporal.Duration.from({
              // Negative `timeSavedSec` is time saved; need to negate it
              seconds: -step.timeSavedSec,
            })
          )}
        </S.TimeSavedValue>
        {t`components.controls.optimize.print-time-saved`}
      </S.TimeSavedSubtitle>
    ) : step.subtitle &&
      ["running", "completed", "failed", "warning"].includes(step.status) ? (
      <S.StepSubtitle $status={step.status}>{step.subtitle}</S.StepSubtitle>
    ) : null;

  const viewResultsAction =
    isLegacy &&
    step.step === "OPTIMIZE_THERMALS" &&
    step.status === "completed" ? (
      <TextLink
        to={projectUrls.optimizeThermal(project.id, optimization.job.id)}
        variant="default"
      >
        {t`components.controls.optimize.view-results`}
      </TextLink>
    ) : null;

  const stepStatusText =
    step.status === "running" && step.progress != null ? (
      <S.StepPercentage>{Math.floor(step.progress * 100)}%</S.StepPercentage>
    ) : step.status === "skipped" ? (
      <S.StepStatusText>{t`common.skipped`}</S.StepStatusText>
    ) : step.status === "completed" ? (
      <S.StepCompleteText>{t`common.completed`}</S.StepCompleteText>
    ) : null;

  return (
    <>
      <S.StepItem $status={step.status} $hasSubstep={hasSubstep}>
        <S.StepItemRow>
          <StepSymbol status={step.status} />
          <S.StepItemContent>
            <S.StepName $status={step.status}>
              {step.text ?? step.step}
            </S.StepName>
            {stepSubtitle}
          </S.StepItemContent>
          {viewResultsAction}
          {stepStatusText}
        </S.StepItemRow>
      </S.StepItem>
      {hasSubstep && (
        <OptimizationStepSection
          project={project}
          optimization={optimization}
        />
      )}
    </>
  );
};

const OptimizationSteps = ({
  project,
  optimization,
}: {
  project: Project;
  optimization: Optimization;
}): ReactElement => {
  const [_location, setLocation] = useLocation();
  const addToast = useAddToast();
  const reviseProject = useReviseProject();

  const revision = project.revisions.find(
    (r) => r.id === optimization.revisionId
  );
  const { data: optimizationCreatedRevision } = useOptimizationCreatedRevision(
    project,
    optimization
  );
  const originalGcodeRecord = revision?.gcodeFile?.file;
  const optimizedGcodeRecord = optimization.optimizedGcode ?? undefined;
  const jobComplete = optimization.job.status === "completed";
  const isLegacy =
    jobComplete && optimization.steps.length === 0 && !!optimizedGcodeRecord;

  async function createRevisionFromOptimized() {
    if (!optimizedGcodeRecord) {
      console.error("Optimized G-code result is unexpectedly missing");
      return;
    }
    await reviseProject.mutateAsync({
      projectId: project.id,
      revisionChange: { gcode: optimizedGcodeRecord },
      onSuccess: (newRevision: Revision) => {
        addToast({
          title: t`components.viewer-controls-optimize.revision-number-created ${newRevision.number}`,
          kind: "success",
          timeout: 5_000,
        });
        setLocation(projectUrls.prepare(project.id, newRevision.id));
      },
    });
  }

  const OPTIMIZATION_STEPS = getOptimizationSteps();

  const stepsRaw = optimization.steps;
  const stepsAugmented = stepsRaw.map((step) => {
    const stepInfo = OPTIMIZATION_STEPS.find((s) => s.step === step.step);
    const errorKey = step.status === "failed" ? step.data?.error : null;
    const warningKey = step.status === "completed" ? step.data?.warning : null;
    const status: StepStatus = warningKey != null ? "warning" : step.status;
    const subtitle =
      (errorKey && getStepErrorMessages()[errorKey]) ||
      (warningKey && getStepWarningMessages()[warningKey]) ||
      stepInfo?.subtitles?.[step.status];
    return {
      ...step,
      status,
      kind: stepInfo ? stepInfo.kind : "check",
      text: stepInfo ? stepInfo.text : undefined,
      subtitle,
    };
  });

  const checks = stepsAugmented.filter((s) => s.kind === "check");
  // Always render all optimization steps defined in OPTIMIZATION_STEPS.
  // The server only returns steps that are running/completed/failed, so we
  // merge against the API data: if a step is missing once optimizations have
  // started it was skipped; before that it is still pending.
  const optimizationsFromApi = stepsAugmented.filter(
    (s) => s.kind === "optimization"
  );
  const optimizationsStarted = optimizationsFromApi.length > 0;
  const optimizations = OPTIMIZATION_STEPS.filter(
    (s) => s.kind === "optimization"
  ).map((stepDef) => {
    const apiStep = optimizationsFromApi.find((s) => s.step === stepDef.step);
    let status: StepStatus;

    if (apiStep?.status) {
      status = apiStep.status;
    } else if (isLegacy && stepDef.step === "OPTIMIZE_THERMALS") {
      status = "completed";
    } else if (optimizationsStarted) {
      status = "skipped";
    } else {
      status = "pending";
    }
    const rawData = apiStep?.data;
    const timeSavedSec =
      stepDef.step === "OPTIMIZE_SPEED" &&
      status === "completed" &&
      typeof rawData?.print_time_delta_sec === "number" &&
      rawData.print_time_delta_sec < 0
        ? Math.trunc(rawData.print_time_delta_sec)
        : null;
    return {
      ...stepDef,
      status,
      progress: apiStep?.progress ?? 0,
      subtitle: stepDef.subtitles?.[status],
      timeSavedSec,
    };
  });

  const checksPassed = checks.filter(
    (s) => s.status === "completed" || s.status === "warning"
  ).length;
  const checksFailed = checks.filter((s) => s.status === "failed").length;
  const checksTotal = checks.filter((s) => s.status !== "skipped").length;
  const allChecksPassed =
    checksTotal > 0 &&
    checks.every(
      (s) =>
        s.status === "completed" ||
        s.status === "skipped" ||
        s.status === "warning"
    );
  const anyCheckFailed = checksFailed > 0;

  const allOptsComplete =
    optimizations.some((s) => s.status !== "skipped") &&
    optimizations.every(
      (s) => s.status === "completed" || s.status === "skipped"
    );
  const anyOptFailed = optimizations.some((s) => s.status === "failed");

  const runningOpt = optimizations.find((s) => s.status === "running");

  const isPreparing =
    allChecksPassed &&
    !optimizationsStarted &&
    optimization.job.status === "started";
  const optimizationsSubtitleHidden = !runningOpt && !isPreparing;
  const optimizationsSubtitle = isPreparing ? (
    <>
      <S.RunningSpinner />
      {t`components.controls.optimize.preparing-optimizations`}
    </>
  ) : runningOpt ? (
    t`components.controls.optimize.running-step ${runningOpt.text ?? runningOpt.step} ${runningOpt.progress != null ? Math.floor(runningOpt.progress * 100) : ""}`
  ) : null;

  const checksBadge = isLegacy ? null : (
    <S.SectionCountBadge $failed={anyCheckFailed} $success={allChecksPassed}>
      {anyCheckFailed
        ? t`components.controls.optimize.checks-failed-badge ${checksFailed}`
        : t`components.controls.optimize.checks-badge ${checksPassed} ${checksTotal}`}
    </S.SectionCountBadge>
  );

  const checksPanel = (
    <S.ContentBlock>
      <S.SectionHeaderArea>
        <S.SectionHeader>
          <h2>{t`components.controls.optimize.checks`}</h2>
          {checksBadge}
        </S.SectionHeader>
        <S.SectionSubtitle>
          {t`components.controls.optimize.checks-subtitle`}
        </S.SectionSubtitle>
      </S.SectionHeaderArea>
      <S.StepContainer>
        {isLegacy ? (
          <S.StepItem $status="completed">
            <S.StepItemRow>
              <StepSymbol status="completed" />
              <S.StepItemContent>
                <S.StepName>{t`components.controls.optimize.legacy-check-completed`}</S.StepName>
              </S.StepItemContent>
            </S.StepItemRow>
          </S.StepItem>
        ) : (
          checks.map((step) => (
            <S.StepItem key={step.step} $status={step.status}>
              <S.StepItemRow>
                <StepSymbol status={step.status} />
                <S.StepItemContent>
                  <S.StepName $status={step.status}>
                    {step.text ?? step.step}
                  </S.StepName>
                  {step.subtitle &&
                    (step.status === "running" ||
                      step.status === "completed") && (
                      <S.StepSubtitle $status={step.status}>
                        {step.subtitle}
                      </S.StepSubtitle>
                    )}
                </S.StepItemContent>
                {step.status === "skipped" && (
                  <S.StepStatusText>{t`common.skipped`}</S.StepStatusText>
                )}
              </S.StepItemRow>
              {(step.status === "failed" || step.status === "warning") &&
                step.subtitle && (
                  <>
                    <S.StepDivider $status={step.status} />
                    <S.StepFailedBody>
                      <S.StepSubtitle $status={step.status}>
                        {step.subtitle}
                      </S.StepSubtitle>
                      <S.StepLearnMoreLink
                        $status={step.status}
                        href={docsUrls.page(
                          "basis/basis_optimization_engine/pre_optimization_checks"
                        )}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {t`components.controls.optimize.step.learn-more`}
                        {" →"}
                      </S.StepLearnMoreLink>
                    </S.StepFailedBody>
                  </>
                )}
            </S.StepItem>
          ))
        )}
      </S.StepContainer>
      {allChecksPassed && (
        <S.PanelFooter>
          <Alert kind="success">
            {t`components.controls.optimize.all-checks-passed`}
          </Alert>
        </S.PanelFooter>
      )}
      {anyCheckFailed && (
        <Alert
          kind="error"
          title={t`components.controls.optimize.cannot-proceed`}
        >
          {t`components.controls.optimize.cannot-proceed-subtitle`}
        </Alert>
      )}
    </S.ContentBlock>
  );

  const optimizationsPanel = (
    <S.ContentBlock>
      <S.SectionHeaderArea>
        <S.SectionHeader>
          <h2>{t`components.controls.optimize.optimizations`}</h2>
        </S.SectionHeader>
        <S.SectionSubtitle $hidden={optimizationsSubtitleHidden}>
          {optimizationsSubtitle}
        </S.SectionSubtitle>
      </S.SectionHeaderArea>
      <S.StepContainer>
        {optimizations.map((step) => (
          <OptimizationStepRow
            key={step.step}
            step={step}
            isLegacy={isLegacy}
            project={project}
            optimization={optimization}
          />
        ))}
      </S.StepContainer>
      <S.PanelFooter>
        {allOptsComplete && (
          <Alert
            kind={jobComplete ? "success" : "muted"}
            spinner={!jobComplete}
          >
            {jobComplete
              ? t`components.controls.optimize.all-opts-complete`
              : t`components.controls.optimize.preparing-results`}
          </Alert>
        )}
        {anyOptFailed && (
          <Alert kind="error">
            {t`components.controls.optimize.opts-failed`}
          </Alert>
        )}
        {!optimizationsStarted && jobComplete && !isLegacy && (
          <Alert
            kind="info"
            title={t`components.controls.optimize.no-optimizations-title`}
          >
            {t`components.controls.optimize.no-optimizations-body`}
          </Alert>
        )}
      </S.PanelFooter>
    </S.ContentBlock>
  );

  const nextStepsPanel = (
    <S.ContentBlock>
      <S.SectionHeaderArea>
        <S.SectionHeader>
          <h2>{t`components.controls.optimize.next-steps`}</h2>
        </S.SectionHeader>
      </S.SectionHeaderArea>
      <S.NextStepsContent>
        {originalGcodeRecord && revision && (
          <S.NextStepsItem>
            <S.NextStepsLabel $compact>
              {t`components.viewer-controls.original-gcode`}
              <FileDownloadLink
                href={blobDownloadURL(originalGcodeRecord.id)}
                title={originalGcodeRecord.name}
                download={originalGcodeRecord.name}
                variant="plain"
              />
            </S.NextStepsLabel>
            <TextLink
              variant="default"
              to={projectUrls.prepare(project.id, revision.id)}
            >
              <span>{t`common.revision`}</span>
              <NeverUnderlineSpace />
              <NumberBadge number={revision.number} size="md" />
            </TextLink>
          </S.NextStepsItem>
        )}
        <S.NextStepsItem>
          <S.NextStepsLabel $muted={!jobComplete} $compact>
            {t`common.optimized-gcode`}
            <FileDownloadLink
              href={
                optimizedGcodeRecord
                  ? blobDownloadURL(optimizedGcodeRecord.id)
                  : undefined
              }
              title={optimizedGcodeRecord?.name ?? t`common.optimized-gcode`}
              download={optimizedGcodeRecord?.name}
              variant="plain"
            />
          </S.NextStepsLabel>
          {optimizationCreatedRevision ? (
            <TextLink
              variant="default"
              to={projectUrls.prepare(
                project.id,
                optimizationCreatedRevision.id
              )}
            >
              <span>{t`common.revision`}</span>
              <NeverUnderlineSpace />
              <NumberBadge
                number={optimizationCreatedRevision.number}
                size="md"
              />
            </TextLink>
          ) : (
            <PermissionTest requiredPermissions={permissions.projects.write}>
              <S.CreateRevisionButton
                kind="primary"
                onClick={() => void createRevisionFromOptimized()}
                disabled={!jobComplete || !optimizedGcodeRecord}
              >
                {t`components.controls.optimize.create-revision`}
              </S.CreateRevisionButton>
            </PermissionTest>
          )}
        </S.NextStepsItem>
        <S.NextStepsDivider />
        <div>
          <S.NextStepsLabel>
            {t`components.controls.optimize.doc-links-label`}
          </S.NextStepsLabel>
          <S.NextStepsLinks>
            <BlueLink
              to="/docs/basis/basis_optimization_engine/pre_optimization_checks"
              target="_blank"
              rel="noreferrer"
            >
              {t`components.controls.optimize.doc-link-pre-optimization-checks`}
            </BlueLink>
            <BlueLink
              to="/docs/basis/basis_optimization_engine/thermal_optimization"
              target="_blank"
              rel="noreferrer"
            >
              {t`components.controls.optimize.doc-link-thermal-optimization`}
            </BlueLink>
            <BlueLink
              to="/docs/basis/basis_optimization_engine/speed_optimization"
              target="_blank"
              rel="noreferrer"
            >
              {t`components.controls.optimize.doc-link-speed-optimization`}
            </BlueLink>
          </S.NextStepsLinks>
        </div>
      </S.NextStepsContent>
    </S.ContentBlock>
  );

  return (
    <S.Wrapper>
      <S.OptimizePanelsContainer>
        {checksPanel}
        {optimizationsPanel}
        {nextStepsPanel}
      </S.OptimizePanelsContainer>
    </S.Wrapper>
  );
};

const WhatGetsOptimizedSection = (): ReactElement => (
  <S.ContentSection>
    <h2>{t`components.controls.optimize.boe-section-title`}</h2>
    <S.ContentBlockContainer>
      <S.ContentBlock>
        <S.ContentBlockTitle>
          <S.ContentBlockIcon
            variant="LocalFireDepartment"
            $color={palette.tomatoRed}
          />
          {t`components.controls.optimize.boe-prevent-overheating-title`}
        </S.ContentBlockTitle>
        <p>{t`components.controls.optimize.boe-prevent-overheating-body`}</p>
      </S.ContentBlock>
      <S.ContentBlock>
        <S.ContentBlockTitle>
          <S.ContentBlockIcon
            variant="ElectricBolt"
            $color={palette.pencilYellow}
          />
          {t`components.controls.optimize.boe-accelerate-title`}
        </S.ContentBlockTitle>
        <p>{t`components.controls.optimize.boe-accelerate-body`}</p>
      </S.ContentBlock>
      <S.ContentBlock>
        <S.ContentBlockTitle>
          <S.ContentBlockIcon
            variant="CenterFocusWeak"
            $color={palette.cornflowerBlue}
          />
          {t`components.controls.optimize.boe-identify-title`}
        </S.ContentBlockTitle>
        <p>{t`components.controls.optimize.boe-identify-body`}</p>
      </S.ContentBlock>
    </S.ContentBlockContainer>
  </S.ContentSection>
);

const NoOptimizationResults = ({
  revision,
  kind,
  onStartOptimizationJob,
  optimization,
}: {
  revision: Revision;
  kind?: "notStarted" | "queued";
  onStartOptimizationJob?: () => void;
  optimization?: Optimization;
}): ReactElement => {
  return (
    <S.Wrapper>
      {kind === "queued" ? (
        <S.ContentBlockHorizontal>
          <S.QueuedStateContent>
            <S.QueuedSpinner />
            <S.QueuedTextBlock>
              <S.QueuedTitleRow>
                <S.RevisionOrJobNumber
                  title={t`common.job-description ${revision.number} ${optimization!.number}`}
                >
                  #{revision.number}-OP{optimization!.number}
                </S.RevisionOrJobNumber>
                <S.QueuedTitle>{t`components.controls.optimize.queued-title`}</S.QueuedTitle>
              </S.QueuedTitleRow>
              <S.QueuedMessage>
                <div>{t`components.controls.optimize.queued-message ${dateTimeString({ value: optimization!.job.createdAt })}`}</div>
                <div>{t`components.controls.optimize.queued-message-detail`}</div>
              </S.QueuedMessage>
            </S.QueuedTextBlock>
          </S.QueuedStateContent>
        </S.ContentBlockHorizontal>
      ) : (
        <S.NotStartedCard>
          <S.NotStartedCardText>
            <S.NotStartedTitle>
              {t`common.revision`}
              <S.RevisionOrJobNumber>#{revision.number}</S.RevisionOrJobNumber>
              {t`components.controls.optimize.not-started-message`}
            </S.NotStartedTitle>
            <S.NotStartedDescription>
              {t`components.controls.optimize.not-started-description`}
            </S.NotStartedDescription>
          </S.NotStartedCardText>
          <StartOptimizationButton
            revision={revision}
            onStartOptimizationJob={onStartOptimizationJob}
          />
        </S.NotStartedCard>
      )}
      <WhatGetsOptimizedSection />
    </S.Wrapper>
  );
};

type OptimizationDataLoaderProps = {
  /** Project to view. */
  project: Project;

  /**
   * ID of the optimization job to view. If not provided, the latest
   * optimization in the latest revision with one will be used.
   */
  optimizationId?: Uuid;
};

/**
 * Loads the optimization result metadata for the current revision.
 */
const OptimizationDataLoader = ({
  project,
  optimizationId,
}: OptimizationDataLoaderProps): ReactElement | null => {
  const { data: optimizations, isFetching } = useProjectOptimizations(
    project.id,
    { parentOnly: true }
  );

  const optimization = project?.revisions
    ? findLatestOptimization(project.revisions, optimizations, optimizationId)
    : undefined;

  if (!project?.revisions) return null;

  if (!optimization) {
    if (isFetching) return null;
    if (optimizations?.length) {
      return (
        <ViewerOverlayNotice
          title={t`components.controls.optimize.no-optimizations-title`}
        >{t`components.controls.optimize.unrecognized-optimization-message`}</ViewerOverlayNotice>
      );
    }
    return <Redirect to={projectUrls.optimize(project.id)} />;
  }

  return (
    <OptimizationResultLoader project={project} optimization={optimization} />
  );
};

export const OptimizeMainView = ({
  project,
  optimizationId,
}: {
  project: Project;
  optimizationId?: Uuid;
}): ReactElement | null => {
  const { data: optimizations, isFetching: optimizationsFetching } =
    useProjectOptimizations(project.id, { parentOnly: true });
  const [_location, setLocation] = useLocation();

  const revisions = project.revisions;

  const opt = findLatestOptimization(
    revisions ?? [],
    optimizations,
    optimizationId
  );

  const revision = opt
    ? revisions.find((r) => r.id === opt.revisionId)
    : revisions?.at(-1);

  const { canOptimize } = useRevisionMaterials(revision!);
  const createOptimization = useCreateOptimization(revision!);
  const addToast = useAddToast();

  async function startOptimizationJob() {
    try {
      const optimization = await createOptimization.mutateAsync({
        optimizeGcode: canOptimize ?? false,
      });
      setLocation(projectUrls.optimize(project.id, optimization.job.id));
    } catch {
      addToast({
        kind: "danger",
        title: t`components.controls.optimize.start-job-error`,
      });
    }
  }

  // Optimization ID was given but not found among this project's optimizations
  if (
    !opt &&
    optimizationId &&
    optimizations?.length &&
    !optimizationsFetching
  ) {
    return (
      <ViewerOverlayNotice
        title={t`components.controls.optimize.no-optimizations-title`}
      >{t`components.controls.optimize.unrecognized-optimization-message`}</ViewerOverlayNotice>
    );
  }

  const lastRevision = revisions.at(-1);
  if (!lastRevision) return <></>;

  // Still waiting for initial data - render nothing to avoid a flash
  if (!optimizations) return null;

  // No optimizations exist yet
  if (!opt) {
    return (
      <NoOptimizationResults
        revision={lastRevision}
        kind="notStarted"
        onStartOptimizationJob={() => void startOptimizationJob()}
      />
    );
  }

  // Optimization is queued/pending, or started but steps haven't populated yet
  if (
    any(equals(opt.job.status), ["queued", "pending"]) ||
    (opt.job.status === "started" && opt.steps.length === 0)
  ) {
    return (
      <NoOptimizationResults
        optimization={opt}
        revision={revision ?? lastRevision}
        kind="queued"
      />
    );
  }

  // Optimization failed with no failed steps - show error overlay
  if (
    opt.job.status === "failed" &&
    opt.steps.every((s) => s.status !== "failed")
  ) {
    const errors = opt.job.output?.errors ?? [];
    return (
      <ViewerOverlayNotice
        title={t`components.controls.optimize.job-failed-title`}
      >
        {errors.length > 0 ? (
          <S.SimErrorsContainer>
            {errors.map((e, i) => (
              <div key={i}>{Array.isArray(e) ? `[${e[0]}] ${e[1]}` : e}</div>
            ))}
          </S.SimErrorsContainer>
        ) : (
          t`components.controls.optimize.job-failed-message`
        )}
        <ButtonContainer>
          <Button kind="primary" onClick={() => void startOptimizationJob()}>
            {t`components.controls.optimize.try-again`}
          </Button>
        </ButtonContainer>
      </ViewerOverlayNotice>
    );
  }

  // Optimization is running, completed, or failed with step data — show steps
  return <OptimizationSteps project={project} optimization={opt} />;
};

export function StartOptimizationButton({
  revision,
  onStartOptimizationJob = noop,
}: {
  revision: Revision;
  onStartOptimizationJob?: () => void;
}): ReactElement {
  const { mat0, mat1, canOptimize } = useRevisionMaterials(revision);

  return (
    <Button
      kind="primary"
      onClick={() => {
        onStartOptimizationJob?.();
      }}
      disabled={!canOptimize}
      helpContent={
        canOptimize ? undefined : (
          <>
            <div>{t`components.project-detail.optimization-disabled`}</div>
            <ul>
              {mat0 && !mat0.capabilities.optimization && (
                <MaterialName material={mat0} />
              )}
              {mat1 && !mat1.capabilities.optimization && (
                <MaterialName material={mat1} />
              )}
            </ul>
            <PrepareS.MakeChildrenInline>
              {t`components.project-detail.not-optimizable-footer`}
              <OptimizationIcon />
            </PrepareS.MakeChildrenInline>
          </>
        )
      }
    >
      {t`common.optimize-gcode`}
    </Button>
  );
}

type OptimizeViewerControlPanelProps = {
  /**
   * Project to view.
   */
  project: Project;
};

export const OptimizeViewerControlPanel = ({
  project,
}: OptimizeViewerControlPanelProps): ReactElement => {
  const optimizeRoute = useOptimizeRoute();

  return (
    <OptimizationDataLoader
      project={project}
      optimizationId={optimizeRoute?.optimizationId}
    />
  );
};
